import type { PanelVariantName } from './variantMap'

export type ShapeSlot = 'primary' | 'available' | 'other' | 'blocks'
export interface ShapePresentation { slot: ShapeSlot, width: number, height: number }
type Size = [number, number]

const sizes: Record<string, Size> = {
  LargeSquare: [50, 50], Square: [35, 35], SquareFreeform: [35, 35], Quarter: [25, 25], Pixel: [16, 16], Bit: [6, 6],
  LargeHalflapVertical: [25, 50], LargeHalflapHorizontal: [50, 25], HalflapVertical: [12.5, 25], HalflapHorizontal: [25, 12.5],
  ShiplapVertical: [12.5, 50], ShiplapHorizontal: [50, 12.5], MiniShiplapVertical: [6.25, 25], MiniShiplapHorizontal: [25, 6.25],
  LeftBottomTriangle: [25, 25], Small90LeftBottomTriangle: [16, 16], LargeHalfCircleVertical: [25, 50],
  QuarterCircleBottomLeft: [25, 24], HalfCircleVertical: [12.5, 25], SmallHalfCircleVertical: [9, 17],
  Hex: [25, 22], LargeDiamond: [25, 43.75], SmallDiamond: [12.5, 21.875], LargeTriangleTop: [25, 20], SmallTriangleTop: [17, 13],
  Hex90: [25, 22], LargeDiamondHorizontal: [48, 27], SmallDiamondHorizontal: [25, 14], LargeTriangleRight: [24, 27], SmallTriangleRight: [12, 14],
  HexSpaced: [25, 22], SmallDiamondSpaced: [14, 25], SmallDiamond60Spaced: [14, 25], SmallDiamond120Spaced: [14, 25],
  Cube36x36x4Set: [77, 77], Cube36x36x2Set: [77, 77], Cube12x12x4Set: [27, 27], Cube12x12x2Set: [27, 27],
  Rect12x36x4Set: [27, 77], Rect12x36x2Set: [27, 77],
  Rect12x36x4Set90: [77, 27], Rect12x36x2Set90: [77, 27],
  SmallPeak: [12.5, 25], LargeCenterPeak: [12.5, 50], LargeUpperPeak: [12.5, 50],
  WoodSlat4Ft: [25, 50], WoodSlat8Ft: [35, 70],
  SmallPeak90: [12.5, 25], LargeCenterPeak90: [12.5, 50], LargeUpperPeak90: [12.5, 50],
  LargeUpperPeak180: [12.5, 50], LargeUpperPeak270: [12.5, 50],
}

for (const id of ['RightTopTriangle', 'LeftTopTriangle', 'RightBottomTriangle']) sizes[id] = [25, 25]
for (const id of ['Small90RightTopTriangle', 'Small90LeftTopTriangle', 'Small90RightBottomTriangle']) sizes[id] = [16, 16]
for (const id of ['LargeHalfCircleVerticalFlipped']) sizes[id] = [25, 50]
for (const id of ['LargeHalfCircleHorizontal', 'LargeHalfCircleHorizontalFlipped']) sizes[id] = [50, 25]
for (const id of ['QuarterCircleTopLeft', 'QuarterCircleBottomRight', 'QuarterCircleTopRight']) sizes[id] = [25, 24]
for (const id of ['HalfCircleVerticalFlipped']) sizes[id] = [12.5, 25]
for (const id of ['HalfCircleHorizontal', 'HalfCircleHorizontalFlipped']) sizes[id] = [25, 12.5]
for (const id of ['SmallHalfCircleVerticalFlipped']) sizes[id] = [9, 17]
for (const id of ['SmallHalfCircleHorizontal', 'SmallHalfCircleHorizontalFlipped']) sizes[id] = [17, 9]

const decorative = [
  'EngravedBackSlash', 'EngravedForwardSlash', 'EngravedCross', 'EngravedFourSquare', 'EngravedParallel', 'EngravedParallelVertical',
  'EngravedTangent', 'EngravedTangent90', 'EngravedTangent180', 'EngravedTangent270', 'EngravedLargeParallel', 'EngravedLargeParallel90',
  'EngravedZen', 'EngravedZen90', 'EngravedZen180', 'EngravedZen270', 'EngravedConverge', 'EngravedConverge90', 'EngravedConverge180', 'EngravedConverge270',
  'EngravedOffset', 'EngravedOffset90', 'EngravedOffset180', 'EngravedOffset270', 'EngravedTopo', 'EngravedTopo90', 'EngravedTopo180', 'EngravedTopo270',
  'EngravedRiver', 'EngravedRiver90', 'EngravedSubway', 'EngravedSubway90', 'EngravedSubway180', 'EngravedSubway270', 'EngravedKnot',
  'Aspen', 'Aspen90', 'Aspen180', 'Aspen270',
  'Bamboo', 'Bamboo90', 'Bamboo180', 'Bamboo270',
  'Geo', 'Geo90', 'Geo180', 'Geo270',
  'Pinstripe', 'Pinstripe90', 'Pinstripe180', 'Pinstripe270',
  'Blossom', 'Blossom90', 'Blossom180', 'Blossom270',
  'Strand', 'Strand90', 'Strand180', 'Strand270',
]
for (const id of decorative) sizes[id] = [51, 51]

const variants45 = [
  'LargeSquare', 'Square', 'Quarter', 'Pixel', 'Bit', 'LargeHalflapVertical', 'HalflapVertical', 'ShiplapVertical', 'MiniShiplapVertical',
  'LargeHalflapHorizontal', 'HalflapHorizontal', 'ShiplapHorizontal', 'MiniShiplapHorizontal',
  'LeftBottomTriangle', 'RightTopTriangle', 'LeftTopTriangle', 'RightBottomTriangle',
  'Small90LeftBottomTriangle', 'Small90RightTopTriangle', 'Small90LeftTopTriangle', 'Small90RightBottomTriangle',
  'LargeHalfCircleVertical', 'LargeHalfCircleHorizontal', 'LargeHalfCircleVerticalFlipped', 'LargeHalfCircleHorizontalFlipped',
  'QuarterCircleBottomLeft', 'QuarterCircleTopLeft', 'QuarterCircleBottomRight', 'QuarterCircleTopRight',
  'HalfCircleVertical', 'HalfCircleHorizontal', 'HalfCircleVerticalFlipped', 'HalfCircleHorizontalFlipped',
  'SmallHalfCircleVertical', 'SmallHalfCircleHorizontal', 'SmallHalfCircleVerticalFlipped', 'SmallHalfCircleHorizontalFlipped',
  ...decorative.filter(id => id.startsWith('Engraved')),
]
for (const id of variants45) sizes[`${id}45deg`] = sizes[id]
// Exact frame bounds from the current 45-degree Figma panel, node 3368:236302.
Object.assign(sizes, {
  LargeSquare45deg: [70.711, 70.711], Square45deg: [49.497, 49.497], Quarter45deg: [35.355, 35.355], Pixel45deg: [22.627, 22.627],
  // Bit is employee-only and absent from Figma; its 20px source tile uses the same √2 / 4.8 scale.
  Bit45deg: [14.142, 14.142],
  LargeHalflapVertical45deg: [53.033, 53.033], ShiplapVertical45deg: [44.194, 44.194], HalflapVertical45deg: [26.517, 26.517], MiniShiplapVertical45deg: [22.097, 22.097],
  LeftBottomTriangle45deg: [35.355, 35.355], Small90LeftBottomTriangle45deg: [22.627, 22.627],
  LargeHalfCircleVertical45deg: [43.126, 43.227], QuarterCircleBottomLeft45deg: [35.355, 33.941], HalfCircleVertical45deg: [21.563, 21.613], SmallHalfCircleVertical45deg: [10.781, 10.807],
  // Peak tile markers appended to the 45-degree panel's
  // "Shapes available on other grids" row. They share the upright peak
  // footprint; the catalog icons render without rotation.
  SmallPeak45deg: [12.5, 25], LargeCenterPeak45deg: [12.5, 50], LargeUpperPeak45deg: [12.5, 50],
})
// 45-deg dropdown children should match their parent's bounding-box size
for (const id of ['LargeHalfCircleHorizontal45deg', 'LargeHalfCircleVerticalFlipped45deg', 'LargeHalfCircleHorizontalFlipped45deg']) sizes[id] = [43.126, 43.227]
for (const id of ['QuarterCircleTopLeft45deg', 'QuarterCircleBottomRight45deg', 'QuarterCircleTopRight45deg']) sizes[id] = [25, 24]
for (const id of ['HalfCircleHorizontal45deg', 'HalfCircleVerticalFlipped45deg', 'HalfCircleHorizontalFlipped45deg']) sizes[id] = [21.563, 21.613]
for (const id of ['SmallHalfCircleHorizontal45deg', 'SmallHalfCircleVerticalFlipped45deg', 'SmallHalfCircleHorizontalFlipped45deg']) sizes[id] = [10.781, 10.807]
for (const id of decorative) sizes[`${id}Freeform`] = [51, 51]
// Freeform rotated printed children (IDs are e.g. AspenFreeform90)
for (const base of ['Aspen', 'Bamboo', 'Geo', 'Pinstripe', 'Blossom', 'Strand']) {
  for (const deg of ['90', '180', '270']) sizes[`${base}Freeform${deg}`] = [51, 51]
}

for (const id of ['LargeDiamond60', 'LargeDiamond120']) sizes[id] = [27, 48]
for (const id of ['SmallDiamond60', 'SmallDiamond120']) sizes[id] = [14, 25]
sizes.LargeTriangleBottom = [27, 24]
sizes.SmallTriangleBottom = [14, 12]
for (const id of ['LargeDiamondHorizontal60', 'LargeDiamondHorizontal120']) sizes[id] = [48, 27]
for (const id of ['SmallDiamondHorizontal60', 'SmallDiamondHorizontal120']) sizes[id] = [25, 14]
sizes.LargeTriangleLeft = [24, 27]
sizes.SmallTriangleLeft = [12, 14]

const blockIds = new Set(['Cube36x36x4Set', 'Cube36x36x2Set', 'Cube12x12x4Set', 'Cube12x12x2Set', 'Rect12x36x4Set', 'Rect12x36x2Set', 'Rect12x36x4Set90', 'Rect12x36x2Set90'])
const nativeHexIds = new Set(['Hex', 'LargeDiamond', 'LargeDiamond60', 'LargeDiamond120', 'SmallDiamond', 'SmallDiamond60', 'SmallDiamond120', 'LargeTriangleTop', 'LargeTriangleBottom', 'SmallTriangleTop', 'SmallTriangleBottom', 'Hex90', 'LargeDiamondHorizontal', 'LargeDiamondHorizontal60', 'LargeDiamondHorizontal120', 'SmallDiamondHorizontal', 'SmallDiamondHorizontal60', 'SmallDiamondHorizontal120', 'LargeTriangleRight', 'LargeTriangleLeft', 'SmallTriangleRight', 'SmallTriangleLeft', 'HexSpaced', 'SmallDiamondSpaced', 'SmallDiamond60Spaced', 'SmallDiamond120Spaced'])

// Exact Freeform item bounds from Figma 3439:46068.
const freeformSizes: Record<string, Size> = {
  LargeSquare: [50, 50], SquareFreeform: [35, 35], Quarter: [25, 25], Pixel: [16, 16],
  LargeHalflapVertical: [25, 50], ShiplapVertical: [12.5, 50], HalflapVertical: [12.5, 25], MiniShiplapVertical: [6.25, 25],
  LargeHalfCircleVertical: [25, 50], QuarterCircleBottomLeft: [25, 24], HalfCircleVertical: [12.5, 25], SmallHalfCircleVertical: [9, 17],
  LeftBottomTriangle: [25, 25], Small90LeftBottomTriangle: [16, 16], LargeDiamond: [25, 43.75], LargeTriangleTop: [25, 20], SmallTriangleTop: [17, 13],
  Hex: [25, 21.875], SmallDiamond: [12.5, 21.875],
  SmallPeakFreeform: [12.5, 25], LargeCenterPeakFreeform: [12.5, 50], LargeUpperPeakFreeform: [12.5, 50],
  WoodSlat4FtFreeform: [25, 47], WoodSlat8FtFreeform: [35, 70],
  SmallPeakFreeform90: [12.5, 25], LargeCenterPeakFreeform90: [12.5, 50], LargeUpperPeakFreeform90: [12.5, 50],
  LargeUpperPeakFreeform180: [12.5, 50], LargeUpperPeakFreeform270: [12.5, 50],
}

const authoredIconSizes: Record<string, Size> = {
  LargeSquare: [50, 50], Square: [35, 35], SquareFreeform: [35, 35], Quarter: [25, 25], Pixel: [16, 16],
  LargeHalflapVertical: [25, 50], LargeHalflapHorizontal: [50, 25], HalflapVertical: [12.5, 25], HalflapHorizontal: [25, 12.5],
  ShiplapVertical: [12.5, 50], ShiplapHorizontal: [50, 12.5], MiniShiplapVertical: [6.25, 25], MiniShiplapHorizontal: [25, 6.25],
  LargeHalfCircleVertical: [25, 50], QuarterCircleBottomLeft: [25, 24], HalfCircleVertical: [12.5, 25], SmallHalfCircleVertical: [9, 17],
  LeftBottomTriangle: [25, 25], Small90LeftBottomTriangle: [16, 16],
  Hex: [25, 21.875], Hex90: [21.875, 25], HexSpaced: [25, 21.875],
  LargeDiamond: [25, 43.75], SmallDiamond: [12.5, 21.875], LargeTriangleTop: [25, 20], SmallTriangleTop: [17, 13],
  EngravedBackSlash: [35, 35], EngravedCross: [35, 35], EngravedFourSquare: [35, 35], EngravedParallel: [35, 35],
  EngravedLargeParallel: [50, 50], EngravedTangent: [50.032, 50], EngravedZen: [50, 50], EngravedOffset: [50.064, 50],
  EngravedRiver: [50, 50], EngravedTopo: [50.032, 50], EngravedConverge: [50.064, 50], EngravedSubway: [50.159, 50],
  EngravedKnot: [50, 50],
  Aspen: [50.064, 50], Bamboo: [50.064, 50], Geo: [50.064, 50], Pinstripe: [50, 50], Blossom: [50, 50], Strand: [50, 50],
  Cube36x36x4Set: [75, 75], Cube36x36x2Set: [75, 75], Cube12x12x4Set: [25, 25], Cube12x12x2Set: [25, 25],
  Rect12x36x4Set: [25, 75], Rect12x36x2Set: [25, 75],
  SmallPeak: [12.5, 25], LargeCenterPeak: [12.5, 50], LargeUpperPeak: [12.5, 50],
  WoodSlat4Ft: [25, 50], WoodSlat8Ft: [35, 70],
  SmallPeak90: [12.5, 25], LargeCenterPeak90: [12.5, 50], LargeUpperPeak90: [12.5, 50],
  LargeUpperPeak180: [12.5, 50], LargeUpperPeak270: [12.5, 50],
}

const authoredHexIconSizes: Record<string, Size> = {
  Hex: [25, 21.875],
  LargeDiamond: [25, 43.75],
  SmallDiamond: [12.5, 21.875],
  LargeTriangleTop: [25, 20],
  SmallTriangleTop: [17, 13],
}

const authoredHex90IconSizes: Record<string, Size> = {
  Hex90: [21.875, 25],
  LargeDiamondHorizontal: [43.75, 25],
  LargeDiamondHorizontal60: [45, 45],
  LargeDiamondHorizontal120: [45, 45],
  SmallDiamondHorizontal: [21.875, 12.5],
  SmallDiamondHorizontal60: [21.875, 21.875],
  SmallDiamondHorizontal120: [21.875, 21.875],
  LargeTriangleRight: [20, 25],
  SmallTriangleRight: [13, 17],
}

const authoredSpacedHexIconSizes: Record<string, Size> = {
  HexSpaced: [25, 21.875],
  SmallDiamondSpaced: [12.5, 21.875],
  SmallDiamond60Spaced: [12.5, 21.875],
  SmallDiamond120Spaced: [12.5, 21.875],
}

const compact45EngravedIds = new Set([
  'EngravedBackSlash45deg',
  'EngravedCross45deg',
  'EngravedFourSquare45deg',
  'EngravedParallel45deg',
])

function getAuthoredPickerSize(variant: PanelVariantName, shapeId: string): Size | undefined {
  if (variant === '45-square' && compact45EngravedIds.has(shapeId)) return [49.497, 49.497]
  if (variant === '45-square' && shapeId.startsWith('Engraved')) return [50 * Math.SQRT2, 50 * Math.SQRT2]
  if (variant === '45-square') return sizes[shapeId]
  if (variant === 'freeform') return freeformSizes[shapeId] || authoredIconSizes[shapeId.replace(/Freeform$/, '')]
  if (variant === 'hex') return authoredHexIconSizes[shapeId] || authoredIconSizes[shapeId]
  if (variant === '90-hex') return authoredHex90IconSizes[shapeId] || authoredIconSizes[shapeId]
  if (variant === 'spaced-hex') return authoredSpacedHexIconSizes[shapeId] || authoredIconSizes[shapeId]
  return authoredIconSizes[shapeId]
}

// Non-square peak children placed at a quarter turn: their authored size is the
// upright footprint, but the picker renders the icon rotated 90° (see the
// quarter-turn swap in getPickerIconStyle), so the button box must swap too or
// the wide rotated icon overflows a tall-and-narrow button.
const quarterTurnFitIds = new Set([
  'SmallPeak90', 'LargeCenterPeak90', 'LargeUpperPeak90', 'LargeUpperPeak270',
  'SmallPeakFreeform90', 'LargeCenterPeakFreeform90', 'LargeUpperPeakFreeform90', 'LargeUpperPeakFreeform270',
])

export function getPickerButtonSize(variant: PanelVariantName, shapeId: string) {
  const presentation = getShapePresentation(variant, shapeId)
  const size = getAuthoredPickerSize(variant, shapeId)
  const width = size?.[0] ?? presentation.width
  const height = size?.[1] ?? presentation.height
  if (quarterTurnFitIds.has(shapeId)) return { width: height, height: width }
  return { width, height }
}

export function getBlockDepthLabel(shapeId: string) {
  if (!blockIds.has(shapeId)) return undefined
  return shapeId.includes('x4') ? '4ʺ' : '2ʺ'
}

export function getPickerRowStyle(variant: PanelVariantName, groupId: string, rowIndex: number) {
  if (variant === '45-square' && groupId === 'engraved-tiles') return { columnGap: '24px' }
  if (variant === 'square' && groupId === 'shapes') {
    return { columnGap: '13px' }
  }
  if (variant === 'square' && ['other-grids', 'engraved-tiles', 'printed-tiles', '3d-blocks'].includes(groupId)) return { columnGap: '18px' }
  return {}
}

export function getPickerGroupStyle(variant: PanelVariantName, groupId: string) {
  if (variant === '45-square' && groupId === 'engraved-tiles') return { rowGap: '24px' }
  if (variant === 'square' && ['shapes', 'engraved-tiles'].includes(groupId)) return { rowGap: '20px' }
  if (variant === 'square' && groupId === '3d-blocks') return { rowGap: '14px' }
  return {}
}

export function getPickerChildrenStyle(variant: PanelVariantName) {
  return variant === '45-square' ? { gap: '24px' } : {}
}

export function getPickerStrokeWidth(transform: string | undefined) {
  const scale = Number.parseFloat(transform?.match(/scale\(([^)]+)\)/)?.[1] || '1')
  return `${2.4 / scale}px`
}

const rotatedSquareFitIds = new Set([
  'LargeSquare45deg', 'Square45deg', 'Quarter45deg', 'Pixel45deg', 'Bit45deg',
  'LeftBottomTriangle45deg', 'Small90LeftBottomTriangle45deg', 'QuarterCircleBottomLeft45deg',
  ...decorative.filter(id => id.startsWith('Engraved')).map(id => `${id}45deg`),
])

export function getPickerTransform(shapeId: string, transform: string | undefined) {
  if (!rotatedSquareFitIds.has(shapeId)) return transform
  return `${transform || ''}scale(${Math.SQRT1_2})`
}

export function getPickerIconStyle(variant: PanelVariantName, shapeId: string, transform: string | undefined) {
  if (variant === '45-square') return { width: '100%', height: '100%', transform: transform || '' }
  const presentation = getShapePresentation(variant, shapeId)
  const [width, height] = getAuthoredPickerSize(variant, shapeId) || [presentation.width, presentation.height]
  const normalizedTransform = (transform || '').replace(/scale\([^)]*\)/g, '').trim()
  const rotation = Number.parseFloat(normalizedTransform.match(/rotate\(([-\d.]+)deg\)/)?.[1] || '0')
  const quarterTurn = Math.abs(rotation % 180) === 90
  // Peak icons are true SVGs with a tall viewBox and preserveAspectRatio="meet",
  // so the box must keep the artwork's UPRIGHT aspect (12.5×50) — the rotation
  // then makes it occupy the wide footprint. Swapping the box first (as the arc
  // shapes need) would letterbox the tall art into a sliver. Other quarter-turn
  // shapes (half-circle arcs, etc.) keep the original swap-then-rotate path.
  const keepUprightBox = quarterTurn && quarterTurnFitIds.has(shapeId)
  const size = keepUprightBox
    ? { width: `${width}px`, height: `${height}px` }
    : {
        width: `${quarterTurn ? height : width}px`,
        height: `${quarterTurn ? width : height}px`,
      }
  if (!quarterTurn) return { ...size, transform: normalizedTransform }
  return {
    ...size,
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: `translate(-50%, -50%) ${normalizedTransform}`,
  }
}

export function getShapePresentation(variant: PanelVariantName, shapeId: string): ShapePresentation {
  const size = variant === 'freeform' && freeformSizes[shapeId] ? freeformSizes[shapeId] : sizes[shapeId]
  if (!size) throw new Error(`Unknown right-panel shape layout: ${variant}/${shapeId}`)
  if (blockIds.has(shapeId)) return { slot: 'blocks', width: size[0], height: size[1] }
  if (nativeHexIds.has(shapeId)) {
    const slot: ShapeSlot = variant === 'hex' || variant === '90-hex' || variant === 'spaced-hex' ? 'available' : 'other'
    return { slot, width: size[0], height: size[1] }
  }
  const slot: ShapeSlot = variant === 'square' ? 'primary' : variant === 'hex' || variant === '90-hex' || variant === 'spaced-hex' ? 'other' : 'available'
  return { slot, width: size[0], height: size[1] }
}
